import React from 'react';
import SiteFooter from '../components/SiteFooter';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Home, CreditCard, Upload, Banknote, Smartphone, MessageCircle, Copy, Check } from 'lucide-react';

const OrderConfirmation: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const orderNum = searchParams.get('orderNum') || '';
  const total = searchParams.get('total') || '';
  const discount = Number(searchParams.get('discount') || 0);
  const originalTotal = searchParams.get('originalTotal') || '';
  const deliveryFee = Number(searchParams.get('deliveryFee') || 0);

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  const handleUploadProof = () => {
    navigate(`/payment-proof/${orderNum}`);
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-green-400/30 rounded-full blur-lg opacity-60"></div>
              <CheckCircle size={120} className="text-green-600 relative z-10" strokeWidth={1.5} />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-3">
            {t('order:confirmation.submitted')}
          </h1>
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-6 text-center">
            <div className="flex justify-center mb-3">
              <MessageCircle size={48} className="text-green-600" />
            </div>
            <p className="text-lg font-bold text-green-800 mb-1">{t('order:confirmation.whatsappSent')}</p>
            <p className="text-gray-600">{t('order:confirmation.checkWhatsapp')}</p>
          </div>

          <div className="bg-white rounded-xl border-2 border-brand-200 p-5 mb-6 shadow">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-3">
              <span className="text-lg text-gray-600">{t('order:confirmation.orderNum')}</span>
              <span className="font-bold text-xl text-brand-600">#{orderNum}</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('order:confirmation.orderAmount')}</span>
                <span className="text-gray-500">HK${originalTotal || total}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-green-700 font-medium">{t('order:confirmation.voucherDiscount')}</span>
                  <span className="font-bold text-green-600">−HK${discount}</span>
                </div>
              )}
              {deliveryFee > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('order:confirmation.extraFee')}</span>
                  <span className="font-bold text-brand-600">HK${deliveryFee}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-lg font-bold text-gray-800">{t('order:confirmation.payable')}</span>
                <span className="font-bold text-xl text-brand-600">HK${total}</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <CreditCard size={24} />
              {t('order:confirmation.payTo')}
            </h3>
            <div className="space-y-3 text-lg text-gray-700">
              <div className="flex items-start gap-3">
                <Banknote size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">{t('order:confirmation.dbsAccount')}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-mono">016-000227829</p>
                    <button
                      onClick={() => handleCopy('016-000227829', 'dbs')}
                      className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title={t('order:confirmation.copyAccount')}
                    >
                      {copiedField === 'dbs' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Smartphone size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">{t('order:confirmation.fps')}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-mono">108810334</p>
                    <button
                      onClick={() => handleCopy('108810334', 'fps')}
                      className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title={t('order:confirmation.copyFps')}
                    >
                      {copiedField === 'fps' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">{t('order:confirmation.accountName')}</p>
                  <div className="flex items-center gap-2">
                    <p>DELICIOUS EXPRESS LTD</p>
                    <button
                      onClick={() => handleCopy('DELICIOUS EXPRESS LTD', 'name')}
                      className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title={t('order:confirmation.copyName')}
                    >
                      {copiedField === 'name' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Payment Proof */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-blue-800 mb-3 flex items-center gap-2">
              <Upload size={24} />
              {t('order:confirmation.uploadProof')}
            </h3>
            <div className="text-lg text-gray-700 space-y-2 mb-4">
              <div className="flex items-start gap-2"><CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" /> {t('order:confirmation.uploadHint1')}</div>
              <div className="flex items-start gap-2"><CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" /> {t('order:confirmation.uploadHint2')}</div>
            </div>
            <button
              onClick={handleUploadProof}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xl font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <Upload size={24} />
              {t('order:confirmation.uploadButton')}
            </button>
          </div>

          <button
            onClick={handleBack}
            className="w-full bg-brand-500 hover:bg-brand-600 text-white text-xl font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
          >
            <Home size={24} />
            {t('order:confirmation.backHome')}
          </button>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default OrderConfirmation;
